/**
 * Material TextField initial underline is applied to the `.MuiInput-underline:before` pseudo-element.
 * This style hides the initial underline, but not the ones that show on hover or focus
 *
 * The hovered underline is applied to `.MuiInput-underline:hover:not(.Mui-disabled):before`, and can be hidden using `useHoverUnderlineHidden`
 *
 * The focused underline is applied to `.MuiInput-underline.Mui-focused:after`, and can be hidden using `useFocusedUnderlineHidden`
 *
 * All underlines can be disabled using `useAllUnderlinesHidden`
 *
 * @returns class styles to be passed to `makeStyles` then `TextField.InputProps.classes` for `underline`
 */
export const useUnderlineHidden = () => ({
  underline: {
    '&::before': {
      borderBottomColor: 'transparent',
    },
  },
});

/**
 * Material TextField hover underline is applied to the `.MuiInput-underline:hover:not(.Mui-disabled):before` pseudo-element.
 * This style hides the hover underline, but not the one that show initially, or on focus
 *
 * The initial underline is applied to `.MuiInput-underline:before`, and can be hidden using `useUnderlineHidden`
 *
 * The focused underline is applied to `.MuiInput-underline.Mui-focused:after`, and can be hidden using `useFocusedUnderlineHidden`
 *
 * All underlines can be disabled using `useAllUnderlinesHidden`
 *
 * @returns class styles to be passed to `makeStyles` then `TextField.InputProps.classes` for `disabled` and `underline`
 */
export const useHoverUnderlineHidden = () => ({
  disabled: {},
  underline: {
    '&:hover:not($disabled):before': {
      borderBottom: '2px transparent',
    },
  },
});

/**
 * Material TextField focused underline is applied to the `.MuiInput-underline.Mui-focused:after` pseudo-element.
 * This style hides the focused underline, but not the ones that shows initially, or on hover
 *
 * The initial underline is applied to `.MuiInput-underline:before`, and can be hidden using `useUnderlineHidden`
 *
 * The hover underline is applied to `.MuiInput-underline:hover:not(.Mui-disabled):before`, and can be hidden using `useHoverUnderlineHidden`
 *
 * All underlines can be disabled using `useAllUnderlinesHidden`
 *
 * @returns class styles to be passed to `makeStyles` then `TextField.InputProps.classes` for `underline`
 */
export const useFocusedUnderlineHidden = () => ({
  underline: {
    '&:after': {
      borderBottomColor: 'transparent',
    },
  },
});

/**
 * This style combines the other underline hooks to disable all underline styles on a `TextField`
 *
 * @returns classes to be passed to `TextField.InputProps.classes` for `underline` and `disabled`
 */
export const useAllUnderlinesHidden = () => {
  const initial = useUnderlineHidden();
  const hover = useHoverUnderlineHidden();
  const focused = useFocusedUnderlineHidden();
  return {
    underline: { ...initial.underline, ...hover.underline, ...focused.underline },
    disabled: {
      ...hover.disabled,
    },
  };
};
